import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { Briefcase, Calculator, Crown, Flame, Gauge, Gift, History, Layers, LogOut, Menu, Newspaper, Orbit, ShieldCheck, TrendingDown, TrendingUp, Wheat, X, Zap } from "lucide-react";
import GlobalTicker from "@/components/GlobalTicker";
import Logo3D from "@/components/Logo3D";
import { useRx } from "@/context/AuthContext";
import AlertSettings from "@/components/AlertSettings";

export const NAV = [
  { group: "Markets", items: [
    { to: "/app", label: "Command Center", icon: Gauge, end: true },
    { to: "/app/option-buyer", label: "Option Buyer", icon: TrendingUp },
    { to: "/app/option-seller", label: "Option Seller", icon: TrendingDown },
    { to: "/app/intraday", label: "Stock Intraday", icon: Zap },
    { to: "/app/futures", label: "Futures", icon: Layers },
    { to: "/app/commodity", label: "Multi-Commodity", icon: Wheat },
    { to: "/app/investors", label: "Investors Window", icon: Briefcase },
    { to: "/app/liquid-options", label: "Liquid Options", icon: Flame },
  ]},
  { group: "Insights", items: [
    { to: "/app/news", label: "Live News", icon: Newspaper },
    { to: "/app/performance", label: "Signal History", icon: History },
    { to: "/app/tools", label: "Risk Calculator", icon: Calculator },
    { to: "/app/immersive", label: "Immersive 3D / AR", icon: Orbit },
  ]},
  { group: "Account", items: [
    { to: "/app/pro-desk", label: "Pro Trader Desk", icon: Crown, pro: true },
    { to: "/app/pricing", label: "Plans", icon: ShieldCheck },
    { to: "/app/referral", label: "Refer & Earn", icon: Gift },
  ]},
];

function SideNav({ onNav }) {
  const { isPro } = useRx();
  return (
    <nav className="flex flex-col gap-6 p-4">
      {NAV.map((g) => (
        <div key={g.group}>
          <div className="rx-label px-3 mb-2">{g.group}</div>
          {g.items.map(({ to, label, icon: Icon, end, pro }) => (
            <NavLink key={to} to={to} end={end} onClick={onNav} data-testid={`nav-${to.split("/").pop() || "app"}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors border ${isActive ? "bg-gradient-to-r from-[#00FF66]/10 to-[#007AFF]/5 border-[#00FF66]/20 text-white" : "border-transparent text-gray-400 hover:text-white hover:bg-white/5"}`}>
              <Icon size={16} className={pro ? "text-[#FFCC00]" : ""} />
              <span className="flex-1">{label}</span>
              {pro && !isPro && <span className="text-[9px] font-bold text-[#FFCC00] border border-[#FFCC00]/40 rounded px-1">PRO</span>}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

function Brand({ size = 32 }) {
  return <NavLink to="/" className="flex items-center"><Logo3D size={size} /></NavLink>;
}

const PLAN_STYLE = { pro: "text-[#00FF66] border-[#00FF66]/40 bg-[#00FF66]/10", basic: "text-[#00B8FF] border-[#007AFF]/40 bg-[#007AFF]/10", free: "text-gray-400 border-white/15" };

export default function AppShell() {
  const [open, setOpen] = useState(false);
  const { me, isDemo, logout } = useRx();
  const plan = me?.plan || "free";
  const loc = useLocation();
  return (
    <div className="min-h-screen bg-[#050505] flex relative">
      <div className="rx-ambient" />
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-white/5 bg-[#07090D]/90 backdrop-blur-xl sticky top-0 h-screen overflow-y-auto rx-scroll z-10">
        <div className="h-20 shrink-0 flex items-center px-6 border-b border-white/5"><Brand size={40} /></div>
        <SideNav />
        <div className="mt-auto p-4 m-4 rounded-xl border border-white/5 bg-[#0B0E13] text-[11px] text-gray-500">
          Simulated market feed. Signals are educational and not investment advice.
        </div>
      </aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-nav">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-[#07090D] border-r border-white/10 overflow-y-auto">
            <div className="h-16 flex items-center justify-between px-5 border-b border-white/5">
              <Brand /><button onClick={() => setOpen(false)} data-testid="mobile-nav-close"><X size={18} /></button>
            </div>
            <SideNav onNav={() => setOpen(false)} />
          </div>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col relative z-10">
        <header className="sticky top-0 z-40 rx-glass !border-x-0 !border-t-0">
          <div className="h-14 flex items-center gap-3 px-4 lg:px-8">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-nav-open" aria-label="Open menu"><Menu size={20} /></button>
            <div className="lg:hidden min-w-0"><Brand size={28} /></div>
            <div className="flex-1" />
            <AlertSettings />
            {isDemo && <span className="hidden sm:inline text-[10px] font-bold tracking-widest text-[#007AFF] border border-[#007AFF]/40 rounded-full px-2 py-1" data-testid="demo-badge">REVIEWER MODE</span>}
            {me && plan !== "pro" && (
              <NavLink to="/app/pricing" data-testid="header-upgrade-btn" className="hidden sm:inline-flex text-xs font-bold rounded-full bg-[#00FF66] text-black px-4 py-1.5 hover:shadow-[0_0_20px_rgba(0,255,102,.5)] transition-shadow">Upgrade</NavLink>
            )}
            <span data-testid="plan-badge" className={`text-[10px] font-bold tracking-widest rounded-full px-2.5 py-1 border ${PLAN_STYLE[plan]}`}>
              {me ? plan.toUpperCase() : "…"}
            </span>
            {isDemo ? (
              <button onClick={logout} data-testid="logout-btn" className="flex items-center gap-1 text-xs text-gray-400 hover:text-white"><LogOut size={14} /> Exit</button>
            ) : <UserButton />}
          </div>
          <GlobalTicker />
        </header>
        <main key={loc.pathname} className="flex-1 px-4 lg:px-8 py-6 lg:py-8 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export const PageHeader = ({ eyebrow, title, sub, right }) => (
  <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6 lg:mb-8">
    <div>
      {eyebrow && <div className="rx-label text-[#00FF66] mb-2">{eyebrow}</div>}
      <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight" data-testid="page-title">{title}</h1>
      <div className="h-[2px] w-16 mt-3 rounded-full bg-gradient-to-r from-[#00FF66] to-[#007AFF] shadow-[0_0_12px_rgba(0,255,102,.6)]" />
      {sub && <p className="text-sm text-gray-400 mt-2 max-w-2xl">{sub}</p>}
    </div>
    {right}
  </div>
);

export const Panel = ({ title, right, children, className = "", testId }) => (
  <section className={`rx-card p-4 lg:p-5 ${className}`} data-testid={testId}>
    {(title || right) && (
      <div className="flex items-center justify-between mb-4 gap-2">
        <h3 className="rx-label">{title}</h3>{right}
      </div>
    )}
    {children}
  </section>
);

